/* eslint-disable no-param-reassign */
import { createSlice } from "@reduxjs/toolkit";
import {
  buildShadowTeamSlots,
  getShadowTeamAbsolutePositionOrder,
  normalizeShadowTeamPositionGroup,
} from "../../pages/shadowTeam/utils/formation";
import {
  addShadowTeamPlayer,
  createShadowTeam,
  deleteShadowTeam,
  fetchShadowTeamById,
  fetchShadowTeamFormations,
  fetchShadowTeams,
  fetchShadowTeamPlayerStats,
  removeShadowTeamPlayer,
  searchShadowTeamPlayers,
  updateShadowTeam,
  updateShadowTeamPlayerPlacement,
} from "./shadowTeamService";

const toFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const getPlayerPhoto = (player) => {
  const photoEntry = ensureArray(player?.photos).find(
    (photo) => photo?.full || photo?.image
  );

  return player?.photo || photoEntry?.full || photoEntry?.image || null;
};

const getTrimmedString = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
};

const getNestedNameValue = (value, visited = new WeakSet()) => {
  if (typeof value === "string") {
    return getTrimmedString(value);
  }

  if (!value || typeof value !== "object") {
    return "";
  }

  if (visited.has(value)) {
    return "";
  }

  visited.add(value);

  const directName = [
    value.name,
    value.team_name,
    value.teamName,
    value.club_name,
    value.clubName,
  ]
    .map(getTrimmedString)
    .find(Boolean);

  if (directName) {
    return directName;
  }

  return Object.values(value)
    .map((nestedValue) => getNestedNameValue(nestedValue, visited))
    .find(Boolean) || "";
};

const getEntityId = (value) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  return toFiniteNumber(
    value.id ?? value.team_id ?? value.teamId ?? value.club_id ?? value.clubId
  );
};

const getEntityLogo = (value) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value.logo ?? value.team_logo ?? value.teamLogo ?? value.club_logo ?? value.clubLogo ?? null;
};

const resolvePlayerTeam = (player) => {
  if (!player || typeof player !== "object") {
    return null;
  }

  const candidateGroups = [
    player.team,
    player.current_team,
    player.currentTeam,
    player.current_club,
    player.currentClub,
    player.club,
    player.team?.team,
    player.club?.club,
    Array.isArray(player.teams) ? player.teams[0] : null,
    Array.isArray(player.teams) ? player.teams[0]?.team : null,
    Array.isArray(player.team) ? player.team[0] : null,
    Array.isArray(player.team) ? player.team[0]?.team : null,
    Array.isArray(player.club) ? player.club[0] : null,
    Array.isArray(player.club) ? player.club[0]?.club : null,
  ].filter(Boolean);

  const fallbackName = [
    player.team_name,
    player.teamName,
    player.club_name,
    player.clubName,
  ]
    .map(getTrimmedString)
    .find(Boolean);

  const teamName =
    candidateGroups.map((candidate) => getNestedNameValue(candidate)).find(Boolean) ||
    fallbackName ||
    null;

  const teamId =
    candidateGroups.map((candidate) => getEntityId(candidate)).find((value) => value !== null) ??
    toFiniteNumber(player.team_id ?? player.teamId ?? player.club_id ?? player.clubId);

  const teamLogo = candidateGroups.map((candidate) => getEntityLogo(candidate)).find(Boolean) || null;
  const baseTeam =
    player.team && typeof player.team === "object" && !Array.isArray(player.team)
      ? player.team
      : null;

  if (!baseTeam && !teamName && teamId === null && !teamLogo) {
    return null;
  }

  return {
    ...(baseTeam || {}),
    ...(teamId !== null ? { id: teamId } : {}),
    ...(teamLogo ? { logo: teamLogo } : {}),
    ...(teamName ? { name: teamName } : {}),
  };
};

const cloneAssignments = (assignments) =>
  ensureArray(assignments).map((assignment) => ({
    ...assignment,
    player:
      assignment?.player && typeof assignment.player === "object"
        ? { ...assignment.player }
        : assignment?.player,
  }));

const unwrapCollection = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.results)) {
    return payload.results;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.data?.results)) {
    return payload.data.results;
  }

  return [];
};

const normalizeFormationLines = (rawLines) => {
  if (Array.isArray(rawLines)) {
    return rawLines
      .map((line) => {
        if (typeof line === "number" || typeof line === "string") {
          return toFiniteNumber(line);
        }

        if (Array.isArray(line)) {
          return line.length;
        }

        if (line && typeof line === "object") {
          if (Array.isArray(line.positions)) {
            return line.positions.length;
          }

          return toFiniteNumber(
            line.count ?? line.players ?? line.value ?? line.length ?? line.total
          );
        }

        return null;
      })
      .filter((line) => Number.isInteger(line) && line > 0);
  }

  if (typeof rawLines === "string") {
    return rawLines
      .split(/[-/,\s]+/)
      .map((line) => toFiniteNumber(line))
      .filter((line) => Number.isInteger(line) && line > 0);
  }

  return [];
};

const normalizeFormation = (rawFormation) => {
  if (!rawFormation || typeof rawFormation !== "object") {
    return null;
  }

  return {
    id: toFiniteNumber(rawFormation.id),
    name: String(rawFormation.name ?? "").trim(),
    category: rawFormation.category ?? null,
    lines: normalizeFormationLines(rawFormation.lines),
  };
};

const normalizeShadowTeamSummary = (rawTeam) => {
  if (!rawTeam || typeof rawTeam !== "object") {
    return null;
  }

  const normalizedFormation = normalizeFormation(rawTeam.formation);
  const id = toFiniteNumber(rawTeam.id);

  if (id === null) {
    return null;
  }

  const formationId = toFiniteNumber(
    rawTeam.formation_id ?? rawTeam.formationId ?? normalizedFormation?.id
  );

  return {
    id,
    name: String(rawTeam.name ?? "").trim(),
    formationId,
    formationName:
      normalizedFormation?.name ||
      String(rawTeam.formation_name ?? rawTeam.formationName ?? "").trim(),
    formation: normalizedFormation,
  };
};

const normalizePlayer = (sourcePlayer, fallbackPlayerId = null) => {
  const player = sourcePlayer && typeof sourcePlayer === "object" ? sourcePlayer : {};
  const id = toFiniteNumber(
    player.id ?? player.player_id ?? player.playerId ?? fallbackPlayerId
  );

  if (id === null) {
    return null;
  }

  const rawNat = Array.isArray(player.nationality) ? player.nationality[0] : null;

  // positions can come as `positions` (array) or legacy `position` (array)
  const positionsArray = Array.isArray(player.positions)
    ? player.positions
    : Array.isArray(player.position)
    ? player.position
    : [];

  const rawSeasonStats = player.season_stats ?? null;
  const resolvedTeam = resolvePlayerTeam(player);

  return {
    id,
    name: String(player.name ?? "").trim(),
    surname: String(player.surname ?? "").trim(),
    displayName: String(player.display_name ?? "").trim() || null,
    team: resolvedTeam,
    clubName: getTrimmedString(resolvedTeam?.name) || null,
    photos: ensureArray(player.photos),
    photo: getPlayerPhoto(player),
    nationality: rawNat
      ? {
          name: rawNat.name ?? null,
          shortName: rawNat.short_name ?? null,
          flag: rawNat.flag ?? null,
        }
      : null,
    birthDate: player.birth_date ?? null,
    age: toFiniteNumber(player.age) ?? null,
    contractUntil: player.contract?.until ?? player.contract_until ?? null,
    position: positionsArray[0]?.name ?? null,
    seasonStats: rawSeasonStats
      ? {
          goals: toFiniteNumber(rawSeasonStats.goals) ?? null,
          assists: toFiniteNumber(rawSeasonStats.assists) ?? null,
          savedShots: toFiniteNumber(rawSeasonStats.saved_shots) ?? null,
          clearances: toFiniteNumber(rawSeasonStats.clearances) ?? null,
          smrtIndexInternational: rawSeasonStats.smrt_index_international ?? null,
          smrtIndexLeague: rawSeasonStats.smrt_index_league ?? null,
          minutesPlayed: toFiniteNumber(rawSeasonStats.minutes_played) ?? null,
          matchesPlayed: toFiniteNumber(rawSeasonStats.matches_played) ?? null,
        }
      : null,
  };
};

const buildLegacySlotLookup = (formationLines) => {
  return buildShadowTeamSlots(formationLines).reduce((accumulator, slot) => {
    accumulator.set(`${slot.lineIndex}:${slot.positionIndex}`, slot);
    return accumulator;
  }, new Map());
};

const resolveCanonicalPlacement = ({
  positionGroup,
  positionOrder,
  legacyLineIndex,
  legacyPositionIndex,
  legacySlotLookup,
}) => {
  const normalizedPositionGroup = normalizeShadowTeamPositionGroup(positionGroup);
  const normalizedPositionOrder = toFiniteNumber(positionOrder);

  if (normalizedPositionGroup && normalizedPositionOrder !== null) {
    return {
      positionGroup: normalizedPositionGroup,
      positionOrder: normalizedPositionOrder,
    };
  }

  if (legacyLineIndex === null || legacyPositionIndex === null) {
    return null;
  }

  const legacySlot = legacySlotLookup.get(`${legacyLineIndex}:${legacyPositionIndex}`);

  if (!legacySlot) {
    return null;
  }

  return {
    positionGroup: legacySlot.positionGroup,
    positionOrder: getShadowTeamAbsolutePositionOrder(
      legacySlot.positionOrder,
      0,
      legacySlot.positionGroup
    ),
  };
};

const extractAssignments = (rawTeam, formationLines) => {
  const assignments = [];
  const legacySlotLookup = buildLegacySlotLookup(formationLines);

  const pushAssignment = (
    { lineIndex, positionIndex, positionGroup, positionOrder },
    playerCandidate,
    fallbackPlayerId = null
  ) => {
    const player = normalizePlayer(playerCandidate, fallbackPlayerId);
    const legacyLineIndex = toFiniteNumber(lineIndex);
    const legacyPositionIndex = toFiniteNumber(positionIndex);
    const placement = resolveCanonicalPlacement({
      positionGroup,
      positionOrder,
      legacyLineIndex,
      legacyPositionIndex,
      legacySlotLookup,
    });

    if (!player || !placement) {
      return;
    }

    assignments.push({
      player,
      positionGroup: placement.positionGroup,
      positionOrder: placement.positionOrder,
      legacyLineIndex,
      legacyPositionIndex,
    });
  };

  const parseItem = (item, inherited = {}) => {
    if (!item || typeof item !== "object") {
      return;
    }

    const lineIndex =
      item.line_index ??
      item.lineIndex ??
      item.line ??
      item.position_line_index ??
      item?.position?.line_index ??
      inherited.lineIndex;

    const positionIndex =
      item.position_index ??
      item.positionIndex ??
      item.slot_index ??
      item.slotIndex ??
      item.position?.position_index ??
      item.position?.index ??
      inherited.positionIndex;

    const positionGroup =
      item.position_group ??
      item.positionGroup ??
      item.position?.group ??
      inherited.positionGroup;

    const positionOrder =
      item.position_order ??
      item.positionOrder ??
      item.position?.order ??
      item.position?.position_order ??
      inherited.positionOrder;

    const inheritedPlacement = {
      lineIndex,
      positionIndex,
      positionGroup,
      positionOrder,
    };

    if (Array.isArray(item.players)) {
      item.players.forEach((nestedPlayer) => {
        parseItem(nestedPlayer, inheritedPlacement);
      });
      return;
    }

    if (item.player && typeof item.player === "object") {
      pushAssignment(inheritedPlacement, item.player, item.player_id);
      return;
    }

    if (item.platform_player && typeof item.platform_player === "object") {
      pushAssignment(inheritedPlacement, item.platform_player, item.player_id);
      return;
    }

    if (item.player_id || item.id) {
      pushAssignment(inheritedPlacement, item, item.player_id);
    }
  };

  [
    rawTeam?.players,
    rawTeam?.shadow_team_players,
    rawTeam?.assignments,
    rawTeam?.positions,
    rawTeam?.slots,
    rawTeam?.lineup,
  ].forEach((source) => {
    if (!Array.isArray(source)) {
      return;
    }

    source.forEach((item) => parseItem(item));
  });

  const mapSource = rawTeam?.players_by_positions || rawTeam?.playersByPositions;
  if (mapSource && typeof mapSource === "object" && !Array.isArray(mapSource)) {
    Object.entries(mapSource).forEach(([key, value]) => {
      const [lineKey, positionKey] = String(key).split(":");
      const lineIndex = toFiniteNumber(lineKey);
      const positionIndex = toFiniteNumber(positionKey);

      ensureArray(value).forEach((player) => {
        parseItem(player, { lineIndex, positionIndex });
      });
    });
  }

  return assignments;
};

const hasShadowTeamAssignmentsPayload = (rawTeam) => {
  if (!rawTeam || typeof rawTeam !== "object") {
    return false;
  }

  if (
    [
      rawTeam.players,
      rawTeam.shadow_team_players,
      rawTeam.assignments,
      rawTeam.positions,
      rawTeam.slots,
      rawTeam.lineup,
    ].some(Array.isArray)
  ) {
    return true;
  }

  const mapSource = rawTeam.players_by_positions || rawTeam.playersByPositions;
  return !!(mapSource && typeof mapSource === "object" && !Array.isArray(mapSource));
};

const normalizeShadowTeamDetail = (rawTeam, availableFormations) => {
  const summary = normalizeShadowTeamSummary(rawTeam);

  if (!summary) {
    return null;
  }

  const formationFromList =
    availableFormations.find((formation) => formation.id === summary.formationId) || null;

  const mergedFormation =
    summary.formation ||
    formationFromList ||
    {
      id: summary.formationId,
      name: summary.formationName,
      category: null,
      lines: normalizeFormationLines(rawTeam?.formation_lines),
    };

  return {
    ...summary,
    formation: mergedFormation,
    assignments: extractAssignments(rawTeam, mergedFormation?.lines),
  };
};

const updateTeamSummary = (state, normalizedTeam) => {
  if (!normalizedTeam || normalizedTeam.id === null) {
    return;
  }

  const existingIndex = state.teams.findIndex((team) => team.id === normalizedTeam.id);

  if (existingIndex === -1) {
    state.teams.unshift(normalizedTeam);
    return;
  }

  state.teams[existingIndex] = {
    ...state.teams[existingIndex],
    ...normalizedTeam,
  };
};

const initialState = {
  formations: [],
  teams: [],
  selectedTeamId: null,
  selectedTeam: null,
  loading: {
    formations: false,
    teams: false,
    selectedTeam: false,
    createTeam: false,
    updateTeam: false,
    deleteTeam: false,
    addPlayer: false,
    removePlayer: false,
    movePlayer: false,
  },
  errors: {
    formations: null,
    teams: null,
    selectedTeam: null,
    createTeam: null,
    updateTeam: null,
    deleteTeam: null,
    addPlayer: null,
    removePlayer: null,
    movePlayer: null,
  },
  playerSearch: {
    loading: false,
    error: null,
    results: [],
  },
  playerStatsCache: {},
};

export const shadowTeamSlice = createSlice({
  name: "shadowTeam",
  initialState,
  reducers: {
    setSelectedShadowTeamId: (state, action) => {
      state.selectedTeamId = toFiniteNumber(action.payload);
    },
    upsertSelectedTeamAssignment: (state, action) => {
      const shadowTeamId = toFiniteNumber(action.payload?.shadowTeamId);
      const player = normalizePlayer(action.payload?.player);
      const positionGroup = normalizeShadowTeamPositionGroup(action.payload?.positionGroup);
      const positionOrder = toFiniteNumber(action.payload?.positionOrder);

      if (
        !state.selectedTeam ||
        shadowTeamId === null ||
        state.selectedTeam.id !== shadowTeamId ||
        !player ||
        !positionGroup ||
        positionOrder === null
      ) {
        return;
      }

      state.selectedTeam.assignments = ensureArray(state.selectedTeam.assignments)
        .filter((assignment) => toFiniteNumber(assignment?.player?.id) !== player.id)
        .concat({
          player,
          positionGroup,
          positionOrder,
          legacyLineIndex: toFiniteNumber(action.payload?.legacyLineIndex),
          legacyPositionIndex: toFiniteNumber(action.payload?.legacyPositionIndex),
        });

      updateTeamSummary(state, state.selectedTeam);
    },
    removeSelectedTeamAssignment: (state, action) => {
      const shadowTeamId = toFiniteNumber(action.payload?.shadowTeamId);
      const playerId = toFiniteNumber(action.payload?.playerId);

      if (
        !state.selectedTeam ||
        shadowTeamId === null ||
        playerId === null ||
        state.selectedTeam.id !== shadowTeamId
      ) {
        return;
      }

      state.selectedTeam.assignments = ensureArray(state.selectedTeam.assignments).filter(
        (assignment) => toFiniteNumber(assignment?.player?.id) !== playerId
      );

      updateTeamSummary(state, state.selectedTeam);
    },
    clearShadowTeamPlayerSearch: (state) => {
      state.playerSearch.loading = false;
      state.playerSearch.error = null;
      state.playerSearch.results = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShadowTeamFormations.pending, (state) => {
        state.loading.formations = true;
        state.errors.formations = null;
      })
      .addCase(fetchShadowTeamFormations.fulfilled, (state, action) => {
        state.loading.formations = false;
        state.formations = unwrapCollection(action.payload)
          .map((formation) => normalizeFormation(formation))
          .filter(Boolean);

        if (state.selectedTeam) {
          state.selectedTeam = normalizeShadowTeamDetail(state.selectedTeam, state.formations);
        }
      })
      .addCase(fetchShadowTeamFormations.rejected, (state, action) => {
        state.loading.formations = false;
        state.errors.formations = action.payload;
      })
      .addCase(fetchShadowTeams.pending, (state) => {
        state.loading.teams = true;
        state.errors.teams = null;
      })
      .addCase(fetchShadowTeams.fulfilled, (state, action) => {
        state.loading.teams = false;
        state.teams = unwrapCollection(action.payload)
          .map((team) => normalizeShadowTeamSummary(team))
          .filter(Boolean);

        if (!state.teams.length) {
          state.selectedTeamId = null;
          state.selectedTeam = null;
          return;
        }

        const hasSelectedTeam = state.teams.some((team) => team.id === state.selectedTeamId);
        if (!hasSelectedTeam) {
          state.selectedTeamId = state.teams[0].id;
        }
      })
      .addCase(fetchShadowTeams.rejected, (state, action) => {
        state.loading.teams = false;
        state.errors.teams = action.payload;
      })
      .addCase(fetchShadowTeamById.pending, (state) => {
        state.loading.selectedTeam = true;
        state.errors.selectedTeam = null;
      })
      .addCase(fetchShadowTeamById.fulfilled, (state, action) => {
        state.loading.selectedTeam = false;

        const normalizedTeam = normalizeShadowTeamDetail(action.payload, state.formations);
        state.selectedTeam = normalizedTeam;

        if (normalizedTeam?.id !== null && normalizedTeam?.id !== undefined) {
          state.selectedTeamId = normalizedTeam.id;
          updateTeamSummary(state, normalizedTeam);
        }
      })
      .addCase(fetchShadowTeamById.rejected, (state, action) => {
        state.loading.selectedTeam = false;
        state.errors.selectedTeam = action.payload;
      })
      .addCase(createShadowTeam.pending, (state) => {
        state.loading.createTeam = true;
        state.errors.createTeam = null;
      })
      .addCase(createShadowTeam.fulfilled, (state, action) => {
        state.loading.createTeam = false;
        const normalizedTeam = normalizeShadowTeamSummary(action.payload);

        if (normalizedTeam) {
          updateTeamSummary(state, normalizedTeam);
          state.selectedTeamId = normalizedTeam.id;
        }
      })
      .addCase(createShadowTeam.rejected, (state, action) => {
        state.loading.createTeam = false;
        state.errors.createTeam = action.payload;
      })
      .addCase(updateShadowTeam.pending, (state) => {
        state.loading.updateTeam = true;
        state.errors.updateTeam = null;
      })
      .addCase(updateShadowTeam.fulfilled, (state, action) => {
        state.loading.updateTeam = false;

        const normalizedTeam = normalizeShadowTeamDetail(action.payload, state.formations);
        const shouldPreserveAssignments =
          normalizedTeam &&
          state.selectedTeam?.id === normalizedTeam.id &&
          !hasShadowTeamAssignmentsPayload(action.payload);
        const nextTeam =
          shouldPreserveAssignments && normalizedTeam
            ? {
                ...normalizedTeam,
                assignments: cloneAssignments(state.selectedTeam?.assignments),
              }
            : normalizedTeam;

        if (nextTeam) {
          updateTeamSummary(state, nextTeam);
          if (state.selectedTeamId === nextTeam.id) {
            state.selectedTeam = nextTeam;
          }
        }
      })
      .addCase(updateShadowTeam.rejected, (state, action) => {
        state.loading.updateTeam = false;
        state.errors.updateTeam = action.payload;
      })
      .addCase(deleteShadowTeam.pending, (state) => {
        state.loading.deleteTeam = true;
        state.errors.deleteTeam = null;
      })
      .addCase(deleteShadowTeam.fulfilled, (state, action) => {
        state.loading.deleteTeam = false;

        const deletedId = toFiniteNumber(action.payload?.id);
        if (deletedId === null) {
          return;
        }

        state.teams = state.teams.filter((team) => team.id !== deletedId);

        if (state.selectedTeamId === deletedId) {
          state.selectedTeamId = state.teams[0]?.id || null;
          state.selectedTeam = null;
        }
      })
      .addCase(deleteShadowTeam.rejected, (state, action) => {
        state.loading.deleteTeam = false;
        state.errors.deleteTeam = action.payload;
      })
      .addCase(addShadowTeamPlayer.pending, (state) => {
        state.loading.addPlayer = true;
        state.errors.addPlayer = null;
      })
      .addCase(addShadowTeamPlayer.fulfilled, (state) => {
        state.loading.addPlayer = false;
      })
      .addCase(addShadowTeamPlayer.rejected, (state, action) => {
        state.loading.addPlayer = false;
        state.errors.addPlayer = action.payload;
      })
      .addCase(removeShadowTeamPlayer.pending, (state) => {
        state.loading.removePlayer = true;
        state.errors.removePlayer = null;
      })
      .addCase(removeShadowTeamPlayer.fulfilled, (state) => {
        state.loading.removePlayer = false;
      })
      .addCase(removeShadowTeamPlayer.rejected, (state, action) => {
        state.loading.removePlayer = false;
        state.errors.removePlayer = action.payload;
      })
      .addCase(updateShadowTeamPlayerPlacement.pending, (state) => {
        state.loading.movePlayer = true;
        state.errors.movePlayer = null;
      })
      .addCase(updateShadowTeamPlayerPlacement.fulfilled, (state) => {
        state.loading.movePlayer = false;
      })
      .addCase(updateShadowTeamPlayerPlacement.rejected, (state, action) => {
        state.loading.movePlayer = false;
        state.errors.movePlayer = action.payload;
      })
      .addCase(searchShadowTeamPlayers.pending, (state) => {
        state.playerSearch.loading = true;
        state.playerSearch.error = null;
      })
      .addCase(searchShadowTeamPlayers.fulfilled, (state, action) => {
        state.playerSearch.loading = false;
        state.playerSearch.results = unwrapCollection(action.payload);
      })
      .addCase(searchShadowTeamPlayers.rejected, (state, action) => {
        state.playerSearch.loading = false;
        state.playerSearch.error = action.payload;
        state.playerSearch.results = [];
      })
      .addCase(fetchShadowTeamPlayerStats.pending, (state, action) => {
        state.playerStatsCache[action.meta.arg] = { loading: true, data: null };
      })
      .addCase(fetchShadowTeamPlayerStats.fulfilled, (state, action) => {
        const { playerId, data } = action.payload;
        state.playerStatsCache[playerId] = { loading: false, data };
      })
      .addCase(fetchShadowTeamPlayerStats.rejected, (state, action) => {
        state.playerStatsCache[action.meta.arg] = { loading: false, data: null };
      });
  },
});

export const { actions: shadowTeamActions } = shadowTeamSlice;
export const { reducer: shadowTeamReducer } = shadowTeamSlice;
